import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Volume2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default ElevenLabs multilingual voices (work even without voices_read permission)
const DEFAULT_VOICES = [
  { voice_id: 'fd0A3mGhFzxF1Wa55oPi', name: 'Alivan (voz original)', description: 'Voz clonada, cálida y natural', primary: true },
  { voice_id: 'Xb7hH8MSUJpSbSDYk0k2', name: 'Alice', description: 'Confiada y clara' },
  { voice_id: 'XB0fDUnXU5powFXDhCwa', name: 'Charlotte', description: 'Suave y seductora' },
  { voice_id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella', description: 'Cálida y amable' },
  { voice_id: 'pFZP5JQG7iQjIQuC4Bku', name: 'Lily', description: 'Elegante y madura' },
  { voice_id: 'MF3mGyEYCl7XYWbV9V6O', name: 'Elli', description: 'Joven y emotiva' },
  { voice_id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel', description: 'Calmada y clara' },
  { voice_id: 'AZnzlk1XvdvUeBnXmlld', name: 'Domi', description: 'Fuerte y directa' },
];

const VoiceSettings = ({ open, onClose, voiceId, autoplayTTS, onSave }) => {
  const [voices, setVoices] = useState(DEFAULT_VOICES);
  const [selectedVoiceId, setSelectedVoiceId] = useState(voiceId || 'fd0A3mGhFzxF1Wa55oPi');
  const [autoplay, setAutoplay] = useState(autoplayTTS ?? true);
  const [previewingId, setPreviewingId] = useState(null);
  const [loadingVoices, setLoadingVoices] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedVoiceId(voiceId || 'fd0A3mGhFzxF1Wa55oPi');
      setAutoplay(autoplayTTS ?? true);
      loadUserVoices();
    }
  }, [open, voiceId, autoplayTTS]);

  const loadUserVoices = async () => {
    setLoadingVoices(true);
    try {
      const resp = await fetch(`${API}/voices`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        const userVoices = data.voices || [];
        // Merge default + user voices, dedupe by voice_id
        const merged = [...DEFAULT_VOICES];
        for (const v of userVoices) {
          if (!merged.find(x => x.voice_id === v.voice_id)) {
            merged.push({
              voice_id: v.voice_id,
              name: v.name,
              description: v.description || (v.labels?.description) || 'Voz personalizada',
              custom: true
            });
          }
        }
        setVoices(merged);
      }
    } catch (e) {
      // Silently fallback to defaults if permission missing
      console.log('Using default voices only');
    } finally {
      setLoadingVoices(false);
    }
  };

  const previewVoice = async (id) => {
    if (previewingId) return;
    setPreviewingId(id);
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          text: 'Hola, soy Alivan, tu asistente cibernética.',
          voice_id: id
        })
      });
      if (!resp.ok) throw new Error('TTS failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setPreviewingId(null);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setPreviewingId(null);
      };
      await audio.play();
    } catch (e) {
      console.error('Preview failed', e);
      setPreviewingId(null);
    }
  };

  const handleSave = () => {
    onSave({ voice_id: selectedVoiceId, autoplay_tts: autoplay });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="voice-settings-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-[#0F0F0F] border border-neon-red/30 w-full max-w-lg mx-4 max-h-[85vh] overflow-hidden flex flex-col shadow-[0_0_40px_rgba(255,0,51,0.3)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neon-red/20">
              <div>
                <h2 className="font-outfit text-2xl font-bold text-white">Configuración de Voz</h2>
                <p className="font-jetbrains text-xs text-white/50 mt-1">Elige cómo suena Alivan</p>
              </div>
              <button
                onClick={onClose}
                data-testid="close-voice-settings"
                className="p-2 text-white/60 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Autoplay Toggle */}
              <div className="flex items-center justify-between p-4 bg-black/40 border border-neon-red/20">
                <div>
                  <p className="font-outfit text-white font-semibold">Reproducción automática</p>
                  <p className="font-jetbrains text-xs text-white/50 mt-0.5">Alivan habla al responder</p>
                </div>
                <button
                  onClick={() => setAutoplay(!autoplay)}
                  data-testid="autoplay-toggle"
                  className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                    autoplay ? 'bg-neon-red' : 'bg-white/10'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${
                      autoplay ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Voice List */}
              <div>
                <p className="font-outfit text-white font-semibold mb-3">
                  Voces disponibles {loadingVoices && <Loader2 className="inline w-4 h-4 animate-spin ml-2" />}
                </p>
                <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-2">
                  {voices.map((v) => (
                    <div
                      key={v.voice_id}
                      onClick={() => setSelectedVoiceId(v.voice_id)}
                      data-testid={`voice-option-${v.voice_id}`}
                      className={`flex items-center gap-3 p-3 cursor-pointer border transition-all duration-200 ${
                        selectedVoiceId === v.voice_id
                          ? 'border-neon-red bg-neon-red/10 shadow-[0_0_15px_rgba(255,0,51,0.2)]'
                          : 'border-white/10 hover:border-neon-red/50 bg-black/30'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-outfit text-white font-semibold">{v.name}</p>
                          {v.primary && (
                            <span className="px-1.5 py-0.5 text-[10px] font-jetbrains bg-neon-red text-white border border-neon-red">
                              ORIGINAL
                            </span>
                          )}
                          {v.custom && !v.primary && (
                            <span className="px-1.5 py-0.5 text-[10px] font-jetbrains bg-neon-red/20 text-neon-red border border-neon-red/40">
                              CUSTOM
                            </span>
                          )}
                        </div>
                        <p className="font-jetbrains text-xs text-white/50 mt-0.5 truncate">
                          {v.description}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          previewVoice(v.voice_id);
                        }}
                        disabled={previewingId !== null}
                        data-testid={`preview-voice-${v.voice_id}`}
                        className="p-2 text-white/60 hover:text-neon-red disabled:opacity-30 transition-colors"
                      >
                        {previewingId === v.voice_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Volume2 className="w-4 h-4" />
                        )}
                      </button>
                      {selectedVoiceId === v.voice_id && (
                        <Check className="w-5 h-5 text-neon-red" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-neon-red/20 flex justify-end gap-3">
              <button
                onClick={onClose}
                data-testid="cancel-voice-settings"
                className="px-6 py-2 font-outfit text-white/70 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                data-testid="save-voice-settings"
                className="px-6 py-2 bg-neon-red text-white font-outfit font-semibold hover:bg-neon-red/80 transition-all duration-300 shadow-[0_0_15px_rgba(255,0,51,0.3)]"
              >
                Guardar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VoiceSettings;
